import {
  createContext,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react'

/**
 * A one-line summary a ROUTE contributes to the chrome's top bar.
 *
 * `TopBar` already carries the page's identity — the breadcrumb's
 * "Organise / Merchants", which holds the document's only `<h1>`. What it had
 * no way to carry was the page's own count line ("24 system · 0 added by you"),
 * which lived on the canvas beneath a second copy of the title. Sushil moved
 * both up on 2026-08-21; the title needed nothing because the breadcrumb was
 * already saying it, and this context is what the count needed.
 *
 * **Context rather than a prop.** `ChromeLayout` renders `TopBar` as a sibling
 * of the `<Outlet>`, so a routed screen cannot pass anything to it by prop
 * without threading state through the router. This repo's stated split is
 * "server state → TanStack Query, UI state → context + `useReducer`"; a single
 * string needs no reducer, so it is `useState` behind a context.
 *
 * **The value is a plain string, never a node.** A route handing the chrome
 * arbitrary JSX would let any screen paint into the shell, which is the
 * boundary this context exists to keep narrow.
 */
const PageMetaContext = createContext<{
  meta: string | null
  /* The real `useState` setter, not a narrowed `(v: string | null) => void`:
     `usePageMeta`'s cleanup needs the UPDATER form to compare against the
     current value before clearing, and a narrowed type cannot express that. */
  setMeta: Dispatch<SetStateAction<string | null>>
} | null>(null)

export function PageMetaProvider({ children }: { children: ReactNode }) {
  const [meta, setMeta] = useState<string | null>(null)
  return <PageMetaContext.Provider value={{ meta, setMeta }}>{children}</PageMetaContext.Provider>
}

/** Read side — `TopBar`'s. Returns `null` outside a provider rather than
 *  throwing, because `BareLayout` renders `TopBar` nowhere and a pre-login tree
 *  has no provider to find. */
export function usePageMetaValue(): string | null {
  return useContext(PageMetaContext)?.meta ?? null
}

/**
 * Write side — a route's. Publishes `value` for as long as the caller is
 * mounted, and takes `null` while the page has nothing to say yet (a loading
 * state), which renders no line rather than an empty one.
 *
 * **The cleanup only clears what it set.** Route changes can mount the next
 * screen before unmounting the previous one, so an unconditional
 * `setMeta(null)` on unmount would wipe a line the NEXT page had already
 * published — the stale-teardown bug that makes a value flicker away a frame
 * after it appears. Comparing before clearing is what makes the order
 * irrelevant.
 */
export function usePageMeta(value: string | null): void {
  const ctx = useContext(PageMetaContext)
  const setMeta = ctx?.setMeta
  useEffect(() => {
    if (setMeta === undefined) return
    setMeta(value)
    return () => {
      setMeta((current) => (current === value ? null : current))
    }
  }, [setMeta, value])
}
